import type { Id, ProgramText } from "@supermanus/document-model";
import { createId } from "@supermanus/document-model";
import type { TextareaHTMLAttributes } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { DocumentHead } from "./DocumentHead";
import { InlineEditable } from "./InlineEditable";

export interface ProgramTextViewProps {
  texts: ProgramText[];
  readOnly?: boolean;
  productionTitle: string;
  seasonTitle?: string | null;
  episodeTitle: string;
  onChange: (texts: ProgramText[]) => void;
}

export function ProgramTextView({
  texts,
  readOnly,
  productionTitle,
  seasonTitle,
  episodeTitle,
  onChange,
}: ProgramTextViewProps) {
  const [adding, setAdding] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [focusBodyId, setFocusBodyId] = useState<Id | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (adding) nameRef.current?.focus();
  }, [adding]);

  function commitAdd() {
    if (readOnly) return;
    const title = draftTitle.trim();
    const next: ProgramText = {
      id: createId(),
      title,
      body: "",
      sortOrder: texts.length,
    };
    onChange([...texts, next]);
    setDraftTitle("");
    setAdding(false);
    setFocusBodyId(next.id);
  }

  function cancelAdd() {
    setDraftTitle("");
    setAdding(false);
  }

  function patchText(id: Id, patch: Partial<Pick<ProgramText, "title" | "body">>) {
    if (readOnly) return;
    onChange(
      texts.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    );
  }

  function removeText(id: Id) {
    if (readOnly) return;
    const item = texts.find((t) => t.id === id);
    const label = item?.title.trim() || "Uden navn";
    if (!window.confirm(`Fjern programteksten «${label}»?`)) return;
    onChange(
      texts.filter((t) => t.id !== id).map((t, i) => ({ ...t, sortOrder: i })),
    );
  }

  return (
    <div className="sm-paper sm-paper-a4 sm-view-programtext">
      <DocumentHead
        productionTitle={productionTitle}
        seasonTitle={seasonTitle}
        episodeTitle={episodeTitle}
        view="programtext"
      />

      <div className="sm-programtext-toolbar">
        {readOnly ? null : adding ? (
          <form
            className="sm-programtext-add-form"
            onSubmit={(e) => {
              e.preventDefault();
              commitAdd();
            }}
          >
            <input
              ref={nameRef}
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  cancelAdd();
                }
              }}
              placeholder="Navn, fx Kort version"
              aria-label="Navn på programtekst"
            />
            <button type="submit">Tilføj</button>
            <button type="button" onClick={cancelAdd}>
              Annuller
            </button>
          </form>
        ) : (
          <button type="button" onClick={() => setAdding(true)}>
            Tilføj programtekst
          </button>
        )}
      </div>

      {texts.length === 0 && !adding ? (
        <p className="sm-programtext-empty">
          Ingen programtekster endnu. Tilføj en, og giv den et navn.
        </p>
      ) : (
        <div className="sm-programtext-list">
          {texts.map((text) => (
            <article key={text.id} className="sm-programtext-card">
              <header className="sm-programtext-card-head">
                <InlineEditable
                  as="h2"
                  className="sm-programtext-title"
                  value={text.title}
                  onChange={(title) => patchText(text.id, { title })}
                  readOnly={readOnly}
                  placeholder="Uden navn"
                  title="Klik for at omdøbe"
                  allowEmpty
                />
                {!readOnly ? (
                  <button
                    type="button"
                    className="sm-programtext-remove"
                    onClick={() => removeText(text.id)}
                  >
                    Fjern
                  </button>
                ) : null}
              </header>
              <DraftTextarea
                className="sm-programtext-body"
                value={text.body}
                disabled={readOnly}
                placeholder="Skriv programteksten her…"
                autoFocus={focusBodyId === text.id}
                onFocused={() => {
                  if (focusBodyId === text.id) setFocusBodyId(null);
                }}
                onValueChange={(body) => patchText(text.id, { body })}
              />
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function DraftTextarea({
  value,
  onValueChange,
  autoFocus,
  onFocused,
  ...rest
}: {
  value: string;
  onValueChange: (value: string) => void;
  autoFocus?: boolean;
  onFocused?: () => void;
} & Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "value" | "onChange" | "defaultValue" | "autoFocus"
>) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const focusedRef = useRef(false);
  const didAutoFocus = useRef(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${Math.max(el.scrollHeight, 120)}px`;
  }, [value]);

  useEffect(() => {
    const el = ref.current;
    if (!el || focusedRef.current) return;
    if (el.value !== value) el.value = value;
  }, [value]);

  useEffect(() => {
    if (!autoFocus || didAutoFocus.current) return;
    const el = ref.current;
    if (!el) return;
    didAutoFocus.current = true;
    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
    onFocused?.();
  }, [autoFocus, onFocused]);

  return (
    <textarea
      {...rest}
      ref={ref}
      defaultValue={value}
      rows={5}
      onFocus={(e) => {
        focusedRef.current = true;
        rest.onFocus?.(e);
      }}
      onChange={(e) => onValueChange(e.target.value)}
      onBlur={(e) => {
        focusedRef.current = false;
        onValueChange(e.target.value);
        rest.onBlur?.(e);
      }}
    />
  );
}
